import { Injectable } from '@angular/core';
import VectorSource from 'ol/source/Vector';
import { Feature } from 'ol';
import Point from 'ol/geom/Point';
import * as olProj from 'ol/proj';
import * as olStyle from 'ol/style';
import { Style, Fill, Text } from 'ol/style';
import { Map } from 'ol';
import { map } from 'rxjs/operators';

import { MarcatoreService } from '@core/http-gen/services/marcatore.service';
import ClusterSource from 'ol/source/Cluster';
import { Observable } from 'rxjs';
import { FeatureLike } from 'ol/Feature';
import { clusterSourceDistance, createPopUp } from '@core/utils/marcatore.utils';
import { SelettivaMarcatoreDto } from '@core/http-gen/models';
import VectorLayer from 'ol/layer/Vector';

@Injectable({
    providedIn: 'root',
})
export class SelettivaMarcatoriSourceService {
    constructor(private marcatoreService: MarcatoreService) {}

    getSource(timeIntervalDays: number): Observable<ClusterSource> {
        return this.marcatoreService
            .apiMarcatoreSelettivaListByPosizionePost({
                body: {
                    topLeftPoint: { x: 10.519868, y: 46.452181 },
                    topRightPoint: { x: 12.119087, y: 46.576185 },
                    bottomLeftPoint: { x: 10.281776, y: 45.733835 },
                    bottomRightPoint: { x: 11.523124, y: 45.618032 },
                    timeIntervalDays: timeIntervalDays,
                },
            })
            .pipe(
                map((selettive: SelettivaMarcatoreDto[]) => {
                    const features = selettive.map((marcatore) => {
                        const feature = new Feature(
                            new Point(olProj.fromLonLat([marcatore.localizzazionePosizione.x, marcatore.localizzazionePosizione.y])),
                        );
                        feature.set('selettivaData', marcatore);
                        return feature;
                    });

                    const vectorSource = new VectorSource({
                        features: features,
                    });

                    const clusterSource = new ClusterSource({
                        distance: clusterSourceDistance,
                        source: vectorSource,
                    });

                    return clusterSource;
                }),
            );
    }

    openPupUp(map: Map | undefined, feature: FeatureLike | undefined): void {
        if (!map || !feature) {
            return;
        }

        const features = feature.get('features');
        if (!features || features.length === 0) {
            return;
        }

        if (features.length > 1) {
            createPopUp(map, feature, {
                title: features.length + ' Selettive',
                description: features
                    .map((f: Feature) => {
                        const selettivaData = f.get('selettivaData');
                        const toReturn = [];
                        toReturn.push(
                            // eslint-disable-next-line max-len
                            `<p class="card-text mb-0 fw-bold">${selettivaData.corpoNome?.toUpperCase()}: ${selettivaData.eventoTipologia}</p>`,
                        );
                        toReturn.push(`<p class="card-text mb-0">${selettivaData.eventoDettaglio}</p>`);
                        toReturn.push(`<p class="card-text mb-0">${new Date(selettivaData.dataOra).toLocaleString()}</p>`);
                        if (selettivaData.posizionePrecisa === false) {
                            toReturn.push(`<p class="card-text mb-0 text-danger">Posizione non precisa</p>`);
                        }

                        return toReturn.join('');
                    })
                    .join('<hr />'),
                buttons: null,
            });
        } else {
            const selettivaData: SelettivaMarcatoreDto = features[0].get('selettivaData');

            createPopUp(map, feature, {
                title: 'Selettiva',
                description: [
                    `<p class="card-text mb-0 fw-bold">${selettivaData.corpoNome?.toUpperCase()}: ${selettivaData.eventoTipologia}</p>`,
                    `<p class="card-text mb-0">${selettivaData.eventoDettaglio}</p>`,
                    `<p class="card-text mb-0">${new Date(selettivaData.dataOra).toLocaleString()}</p>`,
                    selettivaData.posizionePrecisa === false ? `<p class="card-text mb-0 text-danger">Posizione non precisa</p>` : '',
                ].join(''),
                buttons: [
                    {
                        label: 'Visualizza Dettagli',
                        href: `/alertcalls/${selettivaData.id}`,
                        newPage: true,
                    },
                ],
            });
        }
    }

    getClusterStyle(feature: FeatureLike): Style | Style[] {
        const ICON_SCALE = 0.5;

        const size = feature.get('features').length;

        let textSize = 20;
        if (size > 10) {
            textSize = 17;
        }
        if (size > 100) {
            textSize = 15;
        }
        if (size > 1000) {
            textSize = 13;
        }

        if (size > 1) {
            return [
                new Style({
                    image: new olStyle.Circle({
                        radius: 18,
                        fill: new Fill({ color: '#bf3d3d' }),
                        stroke: new olStyle.Stroke({ color: '#fff', width: 2 }),
                    }),
                }),
                new Style({
                    text: new Text({
                        text: size.toString(),
                        font: `bold ${textSize}px sans-serif`,
                        fill: new Fill({ color: '#fff' }),
                        offsetY: 1,
                    }),
                }),
            ];
        } else {
            return new olStyle.Style({
                image: new olStyle.Icon({
                    anchor: [0.5, 0.5],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'fraction',
                    scale: ICON_SCALE,
                    src: this.getIconForAlertcall(feature.get('features')[0].get('selettivaData').eventoTipologia),
                }),
            });
        }
    }

    /** Get icon for the alert call */
    private getIconForAlertcall(eventType: string): string {
        if (!eventType) {
            return 'assets/images/icons/map/pager.svg';
        }

        eventType = eventType.toLowerCase();

        if (eventType.includes('incendi')) {
            return 'assets/images/icons/map/fire.svg';
        } else if (eventType.includes('incident')) {
            return 'assets/images/icons/map/car-crash.svg';
        } else if (eventType.includes('altro')) {
            return 'assets/images/icons/map/pager.svg';
        } else if (eventType.includes('tecnic')) {
            return 'assets/images/icons/map/wrench-screwdriver.svg';
        }

        return 'assets/images/icons/map/pager.svg';
    }

    filterByDays(allSelettiveLayer: VectorLayer, days: number): ClusterSource {
        const source = allSelettiveLayer.getSource();

        if (!(source instanceof ClusterSource)) {
            return new ClusterSource({ source: new VectorSource() }); // empty fallback
        }

        if (days <= 0) {
            // Return EMPTY source
            return new ClusterSource({
                source: new VectorSource(),
                distance: source.getDistance(),
            });
        }

        const baseSource = source.getSource();

        if (!baseSource) {
            return new ClusterSource({ source: new VectorSource() }); // empty fallback
        }

        // Filter subfeatures based on tipoMarcatore
        const filteredFeatures = baseSource.getFeatures().filter((feature: Feature) => {
            const data = feature.get('selettivaData');
            if (!data || !data.dataOra) {
                return false; // Skip features without data or dataOra
            }
            const dataOra = new Date(data.dataOra);
            const now = new Date();
            const timeDiff = now.getTime() - dataOra.getTime();
            const daysDiff = timeDiff / (1000 * 3600 * 24); // Convert milliseconds to days
            return daysDiff <= days; // Keep features within the specified number of days
        });

        // Create a new VectorSource with the filtered features
        const filteredVectorSource = new VectorSource({
            features: filteredFeatures,
        });

        return new ClusterSource({
            distance: source.getDistance(), // preserve clustering distance
            source: filteredVectorSource,
        });
    }
}
